<template>
  <div class="content-page" :data-testid="`content-page-${page}`">
    <!-- Back button -->
    <button
      class="content-back-btn"
      :aria-label="STRINGS.aria.labels.backToHome"
      data-testid="content-back-button"
      @click="emit('navigate', 'homepage')"
    >
      <i class="pi pi-arrow-left" aria-hidden="true"></i>
      <span>{{ STRINGS.auth.links.backToHome }}</span>
    </button>

    <!-- Heading -->
    <h2 class="content-heading">{{ pageData.title }}</h2>

    <!-- Last updated (privacy + terms only) -->
    <p v-if="'lastUpdated' in pageData" class="content-updated">
      {{ (pageData as { lastUpdated: string }).lastUpdated }}
    </p>

    <!-- About page (structured content) -->
    <div v-if="page === 'about'" class="content-card">
      <h3>{{ STRINGS.legal.pages.about.whatIs.heading }}</h3>
      <p>{{ STRINGS.legal.pages.about.whatIs.body }}</p>

      <hr class="content-divider" />

      <h3>{{ STRINGS.legal.pages.about.attributions.heading }}</h3>
      <p>
        {{ STRINGS.legal.pages.about.attributions.body }}
      </p>
      <ul>
        <li>
          <a href="https://en.wiktionary.org/" target="_blank" rel="noopener noreferrer"
            >Wiktionary.org</a
          >
        </li>
        <li>
          <a href="https://kaikki.org/" target="_blank" rel="noopener noreferrer">kaikki.org</a>
        </li>
        <li>
          <a
            href="https://github.com/tatuylonen/wiktextract"
            target="_blank"
            rel="noopener noreferrer"
            >wiktextract</a
          >
          by Tatu Ylonen
        </li>
      </ul>
      <p>
        {{ STRINGS.legal.pages.about.attributions.datasets }}
      </p>
      <ul>
        <li>
          <a
            href="https://github.com/borb-dev/public-assets"
            target="_blank"
            rel="noopener noreferrer"
            >borb-dev/public-assets</a
          >
        </li>
      </ul>

      <hr class="content-divider" />

      <h3>{{ STRINGS.legal.pages.about.contact.heading }}</h3>
      <p>{{ STRINGS.legal.pages.about.contact.body }}</p>

      <hr class="content-divider" />

      <p class="content-copyright">{{ STRINGS.legal.pages.about.copyright }}</p>
    </div>

    <!-- Privacy / Terms (HTML content) -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-else class="legal-content" v-html="legalHtml"></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { NavigationState } from '@/types/websocket';
import STRINGS from '@/constants/strings';
import privacyHtml from '@/content/privacy-policy.html?raw';
import termsHtml from '@/content/terms-of-use.html?raw';

type LegalPage = 'about' | 'privacy' | 'terms';

const props = defineProps<{
  page: LegalPage;
}>();

const emit = defineEmits<{
  navigate: [target: NavigationState];
}>();

const pageData = computed(() => STRINGS.legal.pages[props.page]);

const legalHtml = computed(() => {
  if (props.page === 'privacy') return privacyHtml;
  if (props.page === 'terms') return termsHtml;
  return '';
});
</script>

<style scoped>
.content-page {
  width: 100%;
  max-width: 740px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.content-back-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--color-arena-text-muted);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  margin-bottom: 1.5rem;
  font-family: var(--font-sans);
  font-size: 0.875rem;
  transition: color 150ms ease;
}

.content-back-btn:hover {
  color: var(--color-arena-text-bright);
}

.content-back-btn i {
  font-size: 0.875rem;
}

.content-heading {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  font-weight: 800;
  color: var(--color-arena-text-bright);
  letter-spacing: 0.06em;
  text-align: center;
  margin-bottom: 0.375rem;
}

.content-updated {
  font-family: var(--font-sans);
  font-size: 0.75rem;
  color: var(--color-arena-text-muted);
  text-align: center;
  margin-bottom: 2.5rem;
  letter-spacing: 0.04em;
}

/* ── About page card ── */
.content-card {
  background: var(--color-arena-surface);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-card);
  padding: 1.5rem;
}

@media (min-width: 768px) {
  .content-card {
    padding: 2rem;
  }
}

.content-card h3 {
  font-family: var(--font-display);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  margin: 1.75rem 0 0.75rem 0;
  letter-spacing: 0.04em;
}

.content-card h3:first-child {
  margin-top: 0;
}

.content-card p {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  line-height: 1.7;
  color: var(--color-arena-text-muted);
  margin: 0 0 1rem 0;
}

.content-card p:last-child {
  margin-bottom: 0;
}

.content-card ul {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  line-height: 1.7;
  color: var(--color-arena-text-muted);
  margin: 0 0 1rem 0;
  padding-left: 1.25rem;
}

.content-card li {
  margin-bottom: 0.375rem;
}

.content-card a {
  color: var(--color-energy-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.content-card a:hover {
  color: var(--color-primary-600);
}

.content-divider {
  border: none;
  border-top: 1px solid var(--color-arena-border);
  margin: 1.75rem 0;
}

.content-copyright {
  font-family: var(--font-sans);
  font-size: 0.75rem;
  color: var(--color-arena-text-dim);
  text-align: center;
  margin: 0;
}

/* ── Legal content (privacy + terms) ── */

/* TOC */
.legal-content :deep(.toc) {
  background: var(--color-arena-surface);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-card);
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
}

.legal-content :deep(.toc-label) {
  font-family: var(--font-display);
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--color-arena-text-muted);
  margin-bottom: 0.75rem;
}

.legal-content :deep(.toc ol) {
  list-style: decimal;
  padding-left: 1.25rem;
  font-size: 0.8125rem;
  line-height: 2;
  color: var(--color-energy-primary);
}

.legal-content :deep(.toc a) {
  color: var(--color-energy-primary);
  text-decoration: none;
}

.legal-content :deep(.toc a:hover) {
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* Cards */
.legal-content :deep(.card) {
  background: var(--color-arena-surface);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-card);
  padding: 1.5rem;
  margin-bottom: 1rem;
}

@media (min-width: 600px) {
  .legal-content :deep(.card) {
    padding: 2rem;
  }
}

.legal-content :deep(.card h2) {
  font-family: var(--font-display);
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-energy-primary);
  margin: 2rem 0 1rem;
  padding-top: 2rem;
  border-top: 1px solid var(--color-arena-border);
}

.legal-content :deep(.card h2:first-child) {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.legal-content :deep(.card h3) {
  font-family: var(--font-display);
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  margin: 1.5rem 0 0.5rem;
  letter-spacing: 0.02em;
}

.legal-content :deep(.card p) {
  font-size: 0.875rem;
  line-height: 1.8;
  color: var(--color-arena-text-muted);
  margin-bottom: 0.875rem;
}

.legal-content :deep(.card p:last-child) {
  margin-bottom: 0;
}

.legal-content :deep(.card ul),
.legal-content :deep(.card ol) {
  font-size: 0.875rem;
  line-height: 1.8;
  color: var(--color-arena-text-muted);
  padding-left: 1.25rem;
  margin-bottom: 0.875rem;
}

.legal-content :deep(.card li) {
  margin-bottom: 0.25rem;
}

.legal-content :deep(.card a) {
  color: var(--color-energy-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.legal-content :deep(.card a:hover) {
  color: var(--color-primary-600);
}

.legal-content :deep(.card strong) {
  color: var(--color-arena-text-bright);
  font-weight: 600;
}

.legal-content :deep(.card em) {
  font-style: italic;
}

/* Summary callout */
.legal-content :deep(.summary) {
  background: var(--color-arena-raised);
  border-left: 2px solid var(--color-energy-primary);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  padding: 0.75rem 1rem;
  margin: 0 0 1.25rem;
}

.legal-content :deep(.summary p) {
  font-size: 0.8125rem;
  font-style: italic;
  margin-bottom: 0;
  color: var(--color-arena-text-muted);
}

/* Notice callout */
.legal-content :deep(.notice) {
  border: 1px solid rgba(91, 127, 164, 0.2);
  border-radius: var(--radius-sm);
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
  line-height: 1.7;
}

/* Tables */
.legal-content :deep(.tbl-wrap) {
  overflow-x: auto;
  margin: 1rem 0 1.25rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-arena-border);
}

.legal-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.legal-content :deep(thead th) {
  background: var(--color-arena-raised);
  color: var(--color-arena-text-bright);
  font-family: var(--font-display);
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.625rem 0.875rem;
  text-align: left;
  border-bottom: 1px solid var(--color-arena-border);
  white-space: nowrap;
}

.legal-content :deep(tbody td) {
  color: var(--color-arena-text-muted);
  padding: 0.5rem 0.875rem;
  border-bottom: 1px solid var(--color-arena-border);
  vertical-align: top;
  line-height: 1.65;
}

.legal-content :deep(tbody tr:last-child td) {
  border-bottom: none;
}

.legal-content :deep(tbody tr:nth-child(even) td) {
  background: rgba(255, 255, 255, 0.015);
}

/* Badges */
.legal-content :deep(.yes) {
  display: inline-block;
  background: rgba(0, 196, 143, 0.12);
  color: var(--color-energy-success);
  border: 1px solid rgba(0, 196, 143, 0.25);
  border-radius: 0.25rem;
  padding: 0.1rem 0.5rem;
  font-family: var(--font-mono);
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.legal-content :deep(.no) {
  display: inline-block;
  color: var(--color-arena-text-dim);
  border: 1px solid var(--color-arena-border);
  border-radius: 0.25rem;
  padding: 0.1rem 0.5rem;
  font-family: var(--font-mono);
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* Contact block */
.legal-content :deep(.contact) {
  background: var(--color-arena-raised);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-sm);
  padding: 1rem 1.25rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: var(--color-arena-text-muted);
  line-height: 2;
}

/* Code */
.legal-content :deep(code) {
  font-family: var(--font-mono);
  font-size: 0.8125em;
  background: var(--color-arena-raised);
  padding: 0.1em 0.35em;
  border-radius: 0.2rem;
  color: var(--color-arena-text-bright);
}

/* Focus styles */
.legal-content :deep(a:focus-visible) {
  outline: 2px solid var(--color-energy-primary);
  outline-offset: 3px;
  border-radius: 2px;
}
</style>
